import { CollapsedTaskGlow } from './CollapsedTaskGlow';
import { TaskProgressBar } from './TaskProgressBar';
import { type ReactNode, useState, useEffect, useRef } from 'react';
import type { AgentTaskGroup, CanvasChatMessage } from '@/pages/canvas/canvas-types';
import { taskProgressOutcome } from '../../pages/canvas/task-progress-outcome';

interface ChatToolProgressCardProps {
  groups: AgentTaskGroup[];
  isStreaming: boolean;
  footer?: ReactNode;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  conceptDraftProgress?: CanvasChatMessage['conceptDraftProgress'];
  conceptCount?: number;
}

const boundedCount = (value: number | undefined) => Number.isFinite(value) ? Math.max(0, Math.floor(value!)) : 0;

function conceptWritingLabel(progress: CanvasChatMessage['conceptDraftProgress'], requestedCount?: number): string | undefined {
  const target = boundedCount(progress?.target) || boundedCount(requestedCount);
  if (!target) return undefined;
  // Count durable accepted rows, not provisional drafts that can be rejected.
  const current = Math.min(target, boundedCount(progress?.kept));
  const verb = progress?.stage === 'saved' ? 'Saved' : progress?.stage === 'validating' ? 'Checking' : 'Writing';
  return `${verb} ${current} of ${target} angle concept${target === 1 ? '' : 's'}`;
}

export function ChatToolProgressCard({ groups, isStreaming, expanded: controlledExpanded, onExpandedChange, footer, conceptDraftProgress, conceptCount }: ChatToolProgressCardProps) {
  const [localExpanded, setLocalExpanded] = useState(false);
  const expanded = controlledExpanded ?? localExpanded;
  const setExpanded = onExpandedChange ?? setLocalExpanded;

  // Flatten steps across groups for a single progress list.
  const outcome = taskProgressOutcome(groups);
  const allSteps = outcome.steps;
  const completedCount = outcome.completed;
  const totalSteps = outcome.total;
  const isActive = isStreaming;
  const isDone = !isActive && totalSteps > 0 && completedCount === totalSteps;
  const hasFailure = outcome.failed;
  const progressPercent = totalSteps > 0 ? Math.round((completedCount / totalSteps) * 100) : 0;

  // Label: use active group's label if running; "Working" while streaming without a group; "Completed N steps" when done.
  const activeGroup = groups.find((g) => g.status === 'active');
  const writerStep = activeGroup?.steps.find(step => step.toolName === 'angle_writer' && step.status === 'running');
  const writerLabel = isActive ? conceptWritingLabel(conceptDraftProgress, conceptCount) : undefined;
  const headerLabel = isDone
    ? `Completed ${completedCount} step${completedCount !== 1 ? 's' : ''}${outcome.recovered.size ? ' after retry' : ''}`
    : activeGroup?.label || groups[groups.length - 1]?.label || 'Working\u2026';

  // Auto-collapse on completion (like ThinkingTaskGroup does).
  const prevDoneRef = useRef(isDone);
  useEffect(() => {
    if (!prevDoneRef.current && isDone) setExpanded(false);
    prevDoneRef.current = isDone;
  }, [isDone]);

  if (groups.length === 0) return null;

  const dotColor = hasFailure ? '#EF4444' : isDone ? '#22c55e' : '#A04F6A';

  return (
    <div
      data-testid="chat-tool-progress-card"
      style={{
        background: '#eceef3',
        position: 'relative',
        overflow: 'hidden',
        isolation: 'isolate',
        borderRadius: 24,
        padding: '20px 24px',
        marginBottom: 12,
        marginTop: 12,
      }}
    >
      {!expanded && isActive && !isDone && !hasFailure && <CollapsedTaskGlow />}
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: expanded ? 16 : 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: dotColor, flexShrink: 0, display: 'inline-block' }} />
          <span style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: 13, color: '#2e3338' }}>
            {headerLabel}
          </span>
        </div>
        <button
          onClick={() => setExpanded(!expanded)}
          style={{
            marginLeft: 'auto',
            marginRight: 12,
            fontSize: 10,
            fontWeight: 700,
            color: '#A04F6A',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          {expanded ? 'Hide process' : 'Show process'}
        </button>
        {totalSteps > 0 && (
          <span style={{ fontSize: 11, fontWeight: 700, color: '#5b5f65' }}>
            {completedCount} / {totalSteps} steps
          </span>
        )}
      </div>

      {expanded && totalSteps > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Progress bar */}
          <TaskProgressBar percent={progressPercent} active={isActive} failed={hasFailure} />

          {/* Step list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {allSteps.map((step) => {
              const isCompleted = step.status === 'success';
              const isRunning = step.status === 'running';
              const isFailed = step.status === 'error';
              return (
                <div key={step.id} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                    {isCompleted ? (
                      <span style={{ color: '#A04F6A', fontSize: 16, lineHeight: 1 }}>&#x25CF;</span>
                    ) : isRunning ? (
                      <span style={{ color: '#A04F6A', fontSize: 16, lineHeight: 1, opacity: 0.6 }}>&#x25D0;</span>
                    ) : isFailed ? (
                      <span style={{ color: '#EF4444', fontSize: 16, lineHeight: 1 }}>&#x2716;</span>
                    ) : (
                      <span style={{ color: '#aeb2b8', fontSize: 16, lineHeight: 1 }}>&#x25CB;</span>
                    )}
                    <span style={{
                      color: isCompleted ? '#2e3338' : isFailed ? '#EF4444' : '#5b5f65',
                      fontWeight: isRunning ? 600 : 400,
                      opacity: (!isCompleted && !isRunning && !isFailed) ? 0.6 : 1,
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {step.id === writerStep?.id && writerLabel ? writerLabel : step.friendlyLabel ?? step.toolName}{outcome.recovered.has(step.id) ? ' (earlier attempt; retried successfully)' : ''}
                    </span>
                    {step.summary && (isCompleted || isFailed) && !step.error && (
                      <span style={{ fontSize: 11, color: '#9CA3AF', flexShrink: 0 }}>
                        {step.summary.length > 50 ? step.summary.slice(0, 50) + '\u2026' : step.summary}
                      </span>
                    )}
                  </div>
                  {isFailed && step.error && (
                    <div role="alert" style={{ marginLeft: 24, fontSize: 11, color: '#B91C1C', lineHeight: 1.4, overflowWrap: 'anywhere' }}>
                      {step.error}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {footer}
    </div>
  );
}
